import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './chart-tooltip.css';

const TOOLTIP_GAP = 12;
const VIEWPORT_PAD = 8;

const ChartTooltip = (props) => {
  const { top, left, title, value, visible } = props;
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const shouldRender = value !== '' && value !== undefined;

  useLayoutEffect(() => {
    if (!shouldRender || !visible || typeof document === 'undefined') {
      setCoords(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const tooltipRect = tooltip?.getBoundingClientRect();
      const width = tooltipRect?.width || 160;
      const height = tooltipRect?.height || 56;

      let nextLeft = anchorRect.left;
      let nextTop = anchorRect.top - height - TOOLTIP_GAP;
      let placement = 'above';

      if (nextTop < VIEWPORT_PAD) {
        nextTop = anchorRect.bottom + TOOLTIP_GAP;
        placement = 'below';
      }

      const maxLeft = window.innerWidth - width / 2 - VIEWPORT_PAD;
      const minLeft = width / 2 + VIEWPORT_PAD;
      nextLeft = Math.min(Math.max(nextLeft, minLeft), maxLeft);

      const maxTop = window.innerHeight - height - VIEWPORT_PAD;
      nextTop = Math.min(Math.max(nextTop, VIEWPORT_PAD), maxTop);

      setCoords({ left: nextLeft, top: nextTop, placement });
    };

    updatePosition();

    const frameId = window.requestAnimationFrame(updatePosition);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [shouldRender, visible, top, left, title, value]);

  if (!shouldRender) {
    return null;
  }

  const anchorTop = top === undefined || top === 'auto' ? 0 : top;
  const anchorLeft = left === undefined || left === 'auto' ? 0 : left;

  return (
    <>
      <span
        ref={anchorRef}
        className="chart-tooltip-anchor"
        style={{ top: anchorTop, left: anchorLeft }}
        aria-hidden="true"
      />
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`chart-tooltip${visible && coords ? ' is-visible' : ''}`}
            style={
              coords
                ? {
                    top: `${coords.top}px`,
                    left: `${coords.left}px`,
                  }
                : undefined
            }
            role="tooltip"
          >
            <h3 className="chart-tooltip-title">{title}</h3>
            <p className="chart-tooltip-value">{value}</p>
          </div>,
          document.body
        )}
    </>
  );
};

ChartTooltip.propTypes = {};

ChartTooltip.defaultProps = {};

export default ChartTooltip;
